import { Outlet, Link, NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { ArrowUpRight, LogOut, Menu, X } from 'lucide-react'
import { useState } from 'react'

export default function Layout() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)

  const closeMenu = () => setMenuOpen(false)
  const handleLogout = () => { closeMenu(); logout(); navigate('/login') }
  const links = [
    { to: '/learn', label: 'Courses' },
    ...(user ? [
      { to: '/dashboard', label: 'Dashboard' },
      { to: '/certificates', label: 'Certificates' },
    ] : []),
    ...(user?.role === 'instructor' || user?.role === 'admin'
      ? [{ to: '/instructor', label: 'Teach' }] : []),
    ...(user?.role === 'admin' ? [{ to: '/admin', label: 'Admin' }] : []),
  ]

  return (
    <div className="min-h-screen flex flex-col bg-brand-bg">
      <header className="site-header sticky top-0 z-50 border-b border-brand-border">
        <nav aria-label="Academy navigation" className="max-w-[1180px] mx-auto px-6 h-16 flex items-center gap-6">
          <Link to="/learn" onClick={closeMenu} className="flex shrink-0 items-center gap-3 no-underline hover:no-underline" aria-label="PacketFive Academy home">
            <img src="/packetfive-logo.png" alt="PacketFive" width="142" height="22" className="block h-[22px] w-auto" />
            <span className="text-brand-muted text-xs font-medium">Academy</span>
          </Link>

          <div className="hidden xl:flex items-center gap-6 ml-auto">
            <ul className="flex items-center gap-6">
              {links.map(link => (
                <li key={link.to}>
                  <NavLink to={link.to} className={({ isActive }) => `site-nav-link${isActive ? ' site-nav-link-active' : ''}`}>
                    {link.label}
                  </NavLink>
                </li>
              ))}
              <li>
                <a href="https://www.packetfive.com/" className="site-parent-link">
                  PacketFive.com <ArrowUpRight size={14} aria-hidden="true" />
                </a>
              </li>
            </ul>
            {user ? (
              <div className="flex items-center gap-3">
                <span className="max-w-[100px] truncate text-sm text-brand-muted" title={user.username}>{user.username}</span>
                <button onClick={handleLogout} className="site-nav-link inline-flex items-center gap-1.5">
                  <LogOut size={14}/> Sign out
                </button>
              </div>
            ) : (
              <>
                <Link to="/login" className="site-nav-link">Sign in</Link>
                <Link to="/register" className="btn-primary">Get started</Link>
              </>
            )}
          </div>

          <button type="button" className="xl:hidden ml-auto flex shrink-0 items-center justify-center w-10 h-9 border border-brand-border rounded-lg text-brand-text"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-expanded={menuOpen} aria-controls="academy-mobile-menu"
            onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={22}/> : <Menu size={22}/>}
          </button>
        </nav>

        {menuOpen && (
          <nav id="academy-mobile-menu" aria-label="Academy mobile navigation" className="xl:hidden border-t border-brand-border px-6 py-4 flex flex-col gap-3 bg-white">
            {links.map(link => (
              <NavLink key={link.to} to={link.to} onClick={closeMenu}
                className={({ isActive }) => `site-nav-link py-1${isActive ? ' site-nav-link-active' : ''}`}>
                {link.label}
              </NavLink>
            ))}
            <a href="https://www.packetfive.com/" className="site-parent-link self-start">PacketFive.com <ArrowUpRight size={14} aria-hidden="true" /></a>
            {user ? (
              <button onClick={handleLogout} className="site-nav-link text-left py-1">Sign out</button>
            ) : (
              <>
                <Link to="/login" onClick={closeMenu} className="site-nav-link py-1">Sign in</Link>
                <Link to="/register" onClick={closeMenu} className="btn-primary self-start">Get started</Link>
              </>
            )}
          </nav>
        )}
      </header>

      {/* ── Content ── */}
      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-brand-border bg-brand-surface py-10">
        <div className="max-w-[1180px] mx-auto px-6 text-sm text-brand-muted">
          <a href="https://github.com/PacketFive/PacketFive-LMS" target="_blank" rel="noopener noreferrer"
            aria-label="Powered by PacketFive LMS - view the source on GitHub"
            className="mb-8 flex flex-col sm:flex-row sm:flex-wrap items-center justify-center gap-x-3 gap-y-2 rounded-xl border-2 border-blue-200 bg-blue-50 px-5 py-5 text-brand-blue hover:border-brand-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-blue">
            <span className="text-base font-semibold text-brand-text">Powered by</span>
            <span className="inline-flex items-center gap-2 text-xl font-extrabold">
              <span>PacketFive LMS</span><ArrowUpRight size={22} aria-hidden="true" />
            </span>
            <span className="sm:basis-full text-center text-sm text-brand-text">Open-source learning management system</span>
          </a>
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-6 mb-8">
            <div>
              <a href="https://www.packetfive.com/" className="inline-block mb-3">
                <img src="/packetfive-logo.png" alt="PacketFive" width="142" height="22" className="block h-[22px] w-auto" />
              </a>
              <p>Computer Science training and development.<br />AI/HPC Hyperscale Infrastructure Engineering.</p>
            </div>
            <div className="flex flex-col gap-2">
              <a href="https://academy.packetfive.com/" className="hover:text-brand-text">PacketFive Academy</a>
              <a href="https://www.packetfive.com/" className="hover:text-brand-text">PacketFive.com</a>
              <a href="https://github.com/PacketFive" target="_blank" rel="noopener" className="hover:text-brand-text">GitHub</a>
            </div>
          </div>
          <div className="border-t border-brand-border pt-5 text-xs">
            <p>© 2025-{new Date().getFullYear()} Packet Five Networks Limited. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
